import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    const authed = localStorage.getItem('is_authed');
    if (!authed) {
      setLoading(false);
      setSubscription(null);
      return;
    }

    try {
      const legacyToken = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/subscription/info`, {
        withCredentials: true,
        headers: legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {},
      });
      setSubscription(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError(err.response?.data?.detail || 'Failed to load subscription');
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch subscription on mount and when auth changes (login/logout)
  useEffect(() => {
    fetchSubscription();
    
    // Listen for storage changes (auth added/removed in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'is_authed' || e.key === 'token') {
        fetchSubscription();
      }
    };
    
    // Listen for custom event when auth state changes (same tab)
    const handleTokenChange = () => {
      fetchSubscription();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tokenChanged', handleTokenChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenChanged', handleTokenChange);
    };
  }, [fetchSubscription]);

  const hasFeature = useCallback((featureKey) => {
    if (!subscription?.features) return false;
    return subscription.features[featureKey]?.has_access === true;
  }, [subscription]);

  const getFeatureInfo = useCallback((featureKey) => {
    if (!subscription?.features) return null;
    return subscription.features[featureKey] || null;
  }, [subscription]);

  const canCreateJob = useCallback(() => {
    if (!subscription?.limits?.jobs) return true;
    if (subscription.limits.jobs.unlimited) return true;
    return subscription.limits.jobs.remaining > 0;
  }, [subscription]);

  const canAddUser = useCallback(() => {
    if (!subscription?.limits?.users) return true;
    if (subscription.limits.users.unlimited) return true;
    return subscription.limits.users.remaining > 0;
  }, [subscription]);

  const getJobsRemaining = useCallback(() => {
    if (!subscription?.limits?.jobs) return { used: 0, limit: 50, remaining: 50 };
    return subscription.limits.jobs;
  }, [subscription]);

  const getUsersRemaining = useCallback(() => {
    if (!subscription?.limits?.users) return { used: 0, limit: 1, remaining: 1 };
    return subscription.limits.users;
  }, [subscription]);

  const tierName = subscription?.tier_name || 'Free';
  const tier = subscription?.tier || 'starter';
  const isPro = tier === 'professional' || tier === 'enterprise';
  const isEnterprise = tier === 'enterprise';

  const value = {
    subscription,
    loading,
    error,
    refresh: fetchSubscription,
    hasFeature,
    getFeatureInfo,
    canCreateJob,
    canAddUser,
    getJobsRemaining,
    getUsersRemaining,
    tierName,
    tier,
    isPro,
    isEnterprise,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
